import React, { useState, memo, useMemo, useEffect } from 'react';
import { useTranslation } from "core/hooks/useTranslation";
import { useLocation } from "react-router-dom";

import { Row, Col } from "core/ui-kit/layout";
import Form from "core/ui-kit/form";
import Modal from "core/ui-kit/modal";

import { POPUP_SIZE } from "core/constants/common";

import { isFormChanged } from "utils/form";

import { FORM_FIELD_NAMES } from './constants';

import brandService from 'core/services/apiServices/brandService';
import showSuccess from 'core/helpers/common/showSuccess';
import FormRulesBuilder from 'core/helpers/common/formRulesBuilder';

import NAME_REGEXP from 'core/constants/regexp/nameRegexp';

import useLanguages from 'hooks/useLanguages';
import { PROJECT_PRODUCT_TYPE, PROJECT_PRODUCT_TYPE_TRANSLATIONS } from 'core/constants/project/productType';

const AddEditBrandLanding = ({
    isVisible,
    landing,
    onSave,
    onClose
}) => {
    const { t } = useTranslation();
    const [formInstance] = Form.useForm();

    const { search } = useLocation();

    const [ languages ] = useLanguages();

    /** Is Edit Mode */
    const editMode = landing !== null;

    const [isFormTouched, setIsFormTouched] = useState(!editMode);

    const [isSaving, setIsSaving] = useState(false);

    const brandId = (new URLSearchParams(search)).get("brandId");

    const defaultValues = editMode ? {
        [FORM_FIELD_NAMES.SUB_DOMAIN]: landing.subDomain,
        [FORM_FIELD_NAMES.RELATIVE]: landing.relative,
        [FORM_FIELD_NAMES.LANGUAGE_CODE]: landing.languageCode,
        [FORM_FIELD_NAMES.PRODUCT]: landing.product,
        [FORM_FIELD_NAMES.NAME]: landing.name
    } : {
        [FORM_FIELD_NAMES.SUB_DOMAIN]: "",
        [FORM_FIELD_NAMES.RELATIVE]: "",
        [FORM_FIELD_NAMES.LANGUAGE_CODE]: null,
        [FORM_FIELD_NAMES.PRODUCT]: null,
        [FORM_FIELD_NAMES.NAME]: ""
    };

    const onOkButtonClick = () => {
        formInstance.submit()
    }

    const handleValuesChange = (_, formValues) => {
        setIsFormTouched(isFormChanged({ ...formValues }, { ...defaultValues }))
    }

    const onSuccess = ({ value, message }) => {
        showSuccess(message);
        onSave(value, editMode);
        onClose();
    }

    const handleFormFinish = formData => {
        setIsSaving(true);
        const d = {
            subDomain: formData[FORM_FIELD_NAMES.SUB_DOMAIN],
            product: formData[FORM_FIELD_NAMES.PRODUCT],
            languageCode: formData[FORM_FIELD_NAMES.LANGUAGE_CODE],
            relative: formData[FORM_FIELD_NAMES.RELATIVE],
            name: formData[FORM_FIELD_NAMES.NAME]
        }

        d.brandId = brandId;

        if(editMode){
            d.id = landing.id;
        }

        if(editMode){
            brandService.saveLanding(d)
                .then(onSuccess)
                .finally(() => setIsSaving(false))
        } else {
            brandService.addLanding(d)
                .then(onSuccess)
                .finally(() => setIsSaving(false))
        }
    };

    const languagesOptions = useMemo(() => {
        return  Object.keys(languages)
            .map(item => (
                { value: item.toUpperCase(), text: t(`backoffice.languages.${item}`) }
            ))
    }, [languages, t])

    const productOptions = useMemo(() => {
        return Object.values(PROJECT_PRODUCT_TYPE)
            .map(item => ({ value: item, text: t(PROJECT_PRODUCT_TYPE_TRANSLATIONS[item]) }))
    }, [t])

    useEffect(() => {
        if(isVisible){
            formInstance.setFieldsValue(defaultValues);
            setIsFormTouched(!editMode);
        }
    }, [isVisible])

    return (
        <Modal
            open={isVisible}
            title={editMode ? t('backoffice.projects.editUrl') : t('backoffice.projects.addUrl')}
            cancelText={t('backoffice.common.cancel')}
            okText={editMode ? t('backoffice.common.save') : t('backoffice.common.add')}
            onOk={onOkButtonClick}
            onCancel={onClose}
            size={POPUP_SIZE.BIG}
            okButtonProps={{ disabled: !isFormTouched }}
            isLoading={isSaving}
        >
            <Form
                form={formInstance}
                onValuesChange={ editMode ? handleValuesChange : undefined}
                onFinish={handleFormFinish}
            >
                <Row gutter={[16, 0]}>
                    <Col span={24}>
                        <div className='rt--flex rt--align-end rt--justify-between rt--form-item-inputs'>
                            <Form.Input
                                label={t('backoffice.projects.url')}
                                placeholder={t('backoffice.projects.subDomain')}
                                name={FORM_FIELD_NAMES.SUB_DOMAIN}
                                className="rt--flex-equal rt--form-item-inputs-left"
                            >
                            </Form.Input>
                            <Form.Item
                                label=" "
                            >
                                <div className='rt--form-item-inputs-separator rt--pl-12 rt--pr-12 rt--flex rt--align-center rt--justify-center'>
                                    <span className='rt--title rt--font-normal rt--font-regular'>{t('backoffice.projects.brandDomain')}</span>
                                </div>
                            </Form.Item>
                            <Form.Input
                                label=" "
                                placeholder="/"
                                name={FORM_FIELD_NAMES.RELATIVE}
                                className="rt--flex-equal rt--form-item-inputs-right"
                            >
                            </Form.Input>
                        </div>
                    </Col>
                    <Col span={12}>
                        <Form.Input
                            label={t('backoffice.projects.name')}
                            placeholder={`${t('backoffice.common.enter')} ${t('backoffice.projects.name')}`}
                            name={FORM_FIELD_NAMES.NAME}
                            rules={
                                new FormRulesBuilder(t).required().minCharactersCount(2).maxCharactersCount(30).regexp(NAME_REGEXP).build()
                            }
                            maxLength={30}
                        />
                    </Col>
                    <Col span={12}>
                        <Form.Select
                            label={t('backoffice.projects.language')}
                            placeholder={`${t('backoffice.common.select')} ${t('backoffice.projects.language')}`}
                            name={FORM_FIELD_NAMES.LANGUAGE_CODE}
                            rules={
                                new FormRulesBuilder(t).required({whitespace: false}).build()
                            }
                            search={true}
                            options={languagesOptions}
                        />
                    </Col>
                    <Col span={12}>
                        <Form.Select
                            label={t('backoffice.projects.product')}
                            placeholder={`${t('backoffice.common.select')} ${t('backoffice.projects.product')}`}
                            name={FORM_FIELD_NAMES.PRODUCT}
                            rules={
                                new FormRulesBuilder(t).required({whitespace: false}).build()
                            }
                            options={productOptions}
                            className="rt--form-item-without-margin"
                        />
                    </Col>
                </Row>
            </Form>
        </Modal>
    )
}

export default memo(AddEditBrandLanding);